// src/components/instructor/ops/BookingDetailActions.js
//
// Pass 57C (items 7/8) — primary Booking Detail operational actions: Publish Early and Cancel. Both use
// the normalized operational authorization (owner/admin/ops), record the actor, and send NO outbound
// notification yet. Publish Early reveals ONLY this booking (does not change the org horizon); Cancel
// retains history (no hard delete), releases assignment/capacity (status→canceled) and captures reason.

import React, { useState } from 'react';
import { db } from '../../../firebase';
import { useAuth } from '../../../AuthContext';
import { cancelBooking, publishBookingEarly } from '../../../utils/booking';
import { canOperateOrganization } from '../../../utils/platformAccess';
import { isSourceBackedCommercial, SOURCE_OWNED_EDIT_MESSAGE } from '../../../utils/sourceAuthority';
import { bookingProvenance } from '../../../utils/bookingProvenance';
import {
  effectiveInstructorScheduleVisibility, isBookingVisibleToInstructorNow, computeInstructorVisibleFrom,
} from '../../../utils/scheduleVisibility';

const fmtDate = (s) => (s ? String(s) : '—');
const fmtStamp = (v) => {
  if (!v) return '';
  const d = typeof v?.toDate === 'function' ? v.toDate() : (v?.seconds ? new Date(v.seconds * 1000) : (v instanceof Date ? v : null));
  return d ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
};

export default function BookingDetailActions({ booking, organizationId, organization = {}, member = null, seasonId, onDone }) {
  const { currentUser, userData } = useAuth() || {};
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [reason, setReason] = useState('');
  if (!booking) return null;
  const canOps = canOperateOrganization(userData, organizationId);
  const isCanceled = booking.status === 'canceled';
  // Pass 82D §6/§10 — a source-backed commercial booking is READ-ONLY for cancellation in Motus (managed by the source
  // schedule); provenance is shown truthfully. Motus-native and non-source bookings keep the normal Cancel control.
  const sourceOwned = isSourceBackedCommercial(booking);
  const prov = bookingProvenance(booking);

  // Visibility state for the assigned instructor (School only; collective/full-season = visible now).
  const policy = member ? effectiveInstructorScheduleVisibility({ organization, member, seasonId }) : { applicable: false };
  const visibleNow = isBookingVisibleToInstructorNow(booking, policy, new Date());
  const publishesOn = booking.instructorVisibleFrom || computeInstructorVisibleFrom(booking, policy);
  const publishedEarly = booking.instructorVisibilityOverride === 'published_early';

  const doPublishEarly = async () => {
    setBusy(true); setMsg('');
    try {
      // Pass 57C (item 1): authoritative fail-closed writer — no direct booking updateDoc from the CTA.
      await publishBookingEarly(db, { bookingId: booking.id || booking.bookingId, userData, actorUserId: currentUser?.uid || '', actorName: userData?.name || '' });
      setMsg('Published early.'); onDone && onDone();
    } catch (e) { setMsg(e?.message || 'Failed'); } finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true); setMsg('');
    try {
      await cancelBooking(db, { booking: { id: booking.id || booking.bookingId, ...booking }, userData, reason,
        actorUserId: currentUser?.uid || '', actorName: userData?.name || '', handledByUserId: currentUser?.uid || '' });
      setMsg('Booking cancelled.'); setConfirmCancel(false); onDone && onDone();
    } catch (e) { setMsg(e?.message || 'Cancel failed'); } finally { setBusy(false); }
  };

  if (!canOps) return null;

  return (
    <div data-testid="booking-detail-actions" style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
      {/* Instructor visibility + Publish Early */}
      {!isCanceled && member && policy.applicable && policy.mode !== 'full_season' && (
        <div>
          Instructor visibility:{' '}
          {publishedEarly ? <span data-testid="published-early-state"><strong>Published early</strong> by {booking.publishedToInstructorByName || 'ops'}{fmtStamp(booking.publishedToInstructorAt) ? ` · ${fmtStamp(booking.publishedToInstructorAt)}` : ''}</span>
            : visibleNow ? <strong>Visible now</strong>
            : <>Publishes {fmtDate(publishesOn)}{' '}
                <button data-testid="publish-early-btn" type="button" disabled={busy} onClick={doPublishEarly}
                  style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', cursor: 'pointer' }}>Publish now</button></>}
        </div>
      )}

      {/* Pass 82D — truthful provenance badge (human labels; combined authority not flattened). */}
      {prov.badges.length > 0 && (
        <div data-testid="booking-provenance" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {prov.badges.map((b, i) => (
            <span key={i} title={b.title} style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: b.tone === 'motus' ? '#EDE9FE' : '#E1F5EE', color: b.tone === 'motus' ? '#4C1D95' : '#085041' }}>{b.label}</span>
          ))}
        </div>
      )}

      {/* Cancellation */}
      {sourceOwned && !isCanceled ? (
        <div data-testid="source-owned-note" style={{ fontSize: 11.5, color: '#92400E', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 6, padding: '6px 9px' }}>
          {SOURCE_OWNED_EDIT_MESSAGE}
        </div>
      ) : !isCanceled ? (
        !confirmCancel ? (
          <button data-testid="cancel-btn" type="button" disabled={busy} onClick={() => setConfirmCancel(true)}
            style={{ alignSelf: 'flex-start', padding: '4px 10px', borderRadius: 4, border: '1px solid #DC2626', color: '#DC2626', background: '#fff', cursor: 'pointer' }}>Cancel booking</button>
        ) : (
          <div style={{ border: '1px solid #FCA5A5', borderRadius: 6, padding: 8, background: '#FEF2F2' }}>
            <div style={{ marginBottom: 6 }}>Cancel this booking? This releases the assignment and removes it from the active schedule. History is kept.</div>
            <input data-testid="cancel-reason" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: '100%', marginBottom: 6, fontSize: 12, padding: 4 }} />
            <button data-testid="cancel-confirm" type="button" disabled={busy} onClick={doCancel} style={{ marginRight: 6, padding: '4px 10px', borderRadius: 4, border: 'none', background: '#DC2626', color: '#fff', cursor: 'pointer' }}>Confirm cancel</button>
            <button type="button" disabled={busy} onClick={() => setConfirmCancel(false)} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Keep</button>
          </div>
        )
      ) : <span style={{ color: '#DC2626' }}>Cancelled{booking.cancellationReason ? ` — ${booking.cancellationReason}` : ''}</span>}

      {msg && <span data-testid="bda-msg" style={{ color: '#6B7280' }}>{msg}</span>}
    </div>
  );
}
